/**
 * Dashboard "Site demand vs stock" (17 #9): the projects ranked by what the stores cannot cover — BOQ lines,
 * remaining, the share stock covers today and the shortage — with "Send shortage to Procurement", which
 * turns the short lines of the chosen projects into purchase requests (Procurement › Site requests) in one
 * go after a server preview (POST /inventory/shortage-requests, dry run first).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronRight, Loader2, MapPin, Send, ShoppingCart } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber, formatPercent, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CardFoot, CardHead, InvCard, InvCheckbox, LinkButton, ProgressBar, numClass, tdClass } from './InventoryUi';
import { formatQty, formatUnitTotals, openBoqPartCount, pcsLabel, plural, siteDemandByProject } from './inventoryModel';

const TOP_PROJECTS = 5;
const DEFAULT_LEAD_DAYS = 14;

const plusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

function SystemTag({ system }) {
  if (!system) return <span className="text-ink-4">—</span>;
  return <span className="inline-flex h-5 items-center whitespace-nowrap rounded border border-line bg-surface-2 px-1.5 text-[10.5px] font-bold text-ink-2">{system}</span>;
}

export default function SiteDemandPanel({ sitesBoq = [], stock = [], projects = [], canRequestPurchase = false, onOpenProject, onOpenSitesBoq, onSent, className }) {
  const rows = useMemo(() => siteDemandByProject(sitesBoq, stock, projects), [projects, sitesBoq, stock]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const top = rows.slice(0, TOP_PROJECTS);
  const shortRows = rows.filter((row) => row.short > 0);
  // A part needed by 3 projects is ONE BOQ line on Sites BOQ (and the Dashboard's Sites waiting KPI) but three
  // project lines here — the footer says both, so the two numbers on the page reconcile.
  const lineCount = rows.reduce((sum, row) => sum + row.lines, 0);
  const partCount = useMemo(() => openBoqPartCount(sitesBoq), [sitesBoq]);

  return (
    <InvCard className={cn('flex flex-col', className)} data-testid="inventory-site-demand">
      <CardHead
        icon={MapPin}
        title={<>Site demand vs stock <span className="ml-1.5 hidden font-sans text-[13px] font-normal text-ink-3 sm:inline">Top projects by shortage</span></>}
        right={(
          <span title={!canRequestPurchase ? 'Needs access to Material (like Request purchase)' : !shortRows.length ? 'Nothing is short' : undefined} className="inline-flex">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-ink"
              disabled={!canRequestPurchase || !shortRows.length}
              onClick={() => setDialogOpen(true)}
              data-testid="site-demand-send-shortage"
            >
              <Send aria-hidden="true" /><span className="hidden sm:inline">Send shortage to Procurement</span><span className="sm:hidden">Send shortage</span>
            </Button>
          </span>
        )}
      />
      {top.length ? (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[320px] border-separate border-spacing-0 text-[13px] text-ink">
            <thead>
              <tr className="text-[11.5px] font-bold text-ink-3">
                <th className="h-9 border-b border-line bg-surface-2 px-5 text-left">Project</th>
                <th className="hidden h-9 border-b border-line bg-surface-2 px-3 text-left md:table-cell">System</th>
                <th className={cn('hidden h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 md:table-cell', numClass)} title="This project's BOQ lines still to deliver — a part needed by several projects counts once in each">BOQ lines</th>
                <th className={cn('hidden h-9 border-b border-line bg-surface-2 px-3 sm:table-cell', numClass)}>Remaining</th>
                <th className="hidden h-9 border-b border-line bg-surface-2 px-3 text-left lg:table-cell">Covered by stock</th>
                <th className={cn('h-9 border-b border-line bg-surface-2 px-3', numClass)}>Short</th>
                <th className="h-9 w-10 border-b border-line bg-surface-2"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {top.map((row) => (
                <tr
                  key={row.id}
                  className="group cursor-pointer [&:hover>td]:bg-surface-hover"
                  onClick={() => onOpenProject?.(row.id)}
                  data-testid={`site-demand-row-${row.id}`}
                >
                  <td className={cn(tdClass, 'px-5 font-semibold')}>
                    <span className="block truncate">{row.name}</span>
                    <span className="block text-[11.5px] font-normal text-ink-3 sm:hidden">{formatUnitTotals(row.units)} remaining</span>
                  </td>
                  <td className={cn(tdClass, 'hidden md:table-cell')}><SystemTag system={row.system} /></td>
                  <td className={cn(tdClass, numClass, 'hidden text-ink-2 md:table-cell')}>{formatNumber(row.lines)}</td>
                  <td className={cn(tdClass, numClass, 'hidden text-ink-2 sm:table-cell')}>{formatUnitTotals(row.units)}</td>
                  <td className={cn(tdClass, 'hidden lg:table-cell')}>
                    <span className="flex items-center gap-2.5">
                      <ProgressBar value={row.coveredPct} tone="green" className="w-[120px]" label={`${row.name} covered by stock`} />
                      <span className="w-12 text-[12px] tabular-nums text-ink-2">{formatPercent(row.coveredPct)}</span>
                    </span>
                  </td>
                  <td className={cn(tdClass, numClass, 'font-bold', row.short > 0 ? 'text-status-red-ink' : 'text-ink-3')} data-testid={`site-demand-short-${row.id}`}>
                    {formatQty(row.short)}
                  </td>
                  <td className={cn(tdClass, 'pr-3 text-right')}>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onOpenProject?.(row.id); }}
                      aria-label={`Open ${row.name} in Sites BOQ`}
                      className="grid h-7 w-7 place-items-center rounded-md text-ink-4 hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:text-ink-3"
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={MapPin}
          title="No site is waiting for items"
          text="Projects with BOQ lines still to deliver show up here, with what the stores can cover."
          data-testid="site-demand-empty"
        />
      )}
      {rows.length ? (
        <CardFoot className="mt-auto justify-between">
          <span data-testid="site-demand-foot">
            Covered = available stock in any location ÷ remaining BOQ · {plural(partCount, 'BOQ line')} ({formatNumber(lineCount)} project {lineCount === 1 ? 'line' : 'lines'}) across {plural(rows.length, 'project')}.
          </span>
          <LinkButton onClick={() => onOpenSitesBoq?.()}>See all in Sites BOQ <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" /></LinkButton>
        </CardFoot>
      ) : null}
      <ShortageDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        rows={shortRows}
        onSent={onSent}
      />
    </InvCard>
  );
}

/**
 * Pick projects + needed-by date → the server previews exactly which lines become purchase requests (lines
 * already requested are skipped) → Send. A preview that went stale (409) is fetched again.
 */
export function ShortageDialog({ open, onOpenChange, rows = [], onSent }) {
  const [selected, setSelected] = useState(() => new Set());
  const [neededBy, setNeededBy] = useState(plusDays(DEFAULT_LEAD_DAYS));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Why the preview changed under the user (409) — kept while the new preview loads (loadPreview clears `error`).
  const [notice, setNotice] = useState('');
  const [sending, setSending] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(rows.slice(0, TOP_PROJECTS).map((row) => row.id)));
    setNeededBy(plusDays(DEFAULT_LEAD_DAYS));
    setPreview(null);
    setError('');
    setNotice('');
    // Only when the dialog opens: the list is fixed while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ids = useMemo(() => rows.map((row) => row.id).filter((id) => selected.has(id)), [rows, selected]);
  const idsKey = ids.join('|');

  const loadPreview = async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!ids.length) { setPreview(null); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/inventory/shortage-requests', { project_ids: ids, needed_by: neededBy, dry_run: true });
      if (requestId === requestRef.current) setPreview(res.data || null);
    } catch (err) {
      if (requestId === requestRef.current) { setPreview(null); setError(getErrorMessage(err, 'Could not prepare the list')); }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(loadPreview, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey, neededBy]);

  const toggle = (id, checked) => {
    setNotice('');
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const send = async () => {
    if (!preview?.plan_key || !preview.request_lines) return;
    setSending(true);
    setError('');
    setNotice('');
    try {
      const res = await api.post('/inventory/shortage-requests', { project_ids: ids, needed_by: neededBy, dry_run: false, plan_key: preview.plan_key });
      notify.success(`Sent ${plural(res.data?.created_requests ?? preview.request_lines, 'purchase request')} to Procurement`, {
        description: `${plural(ids.length, 'project')} · needed on site by ${formatDate(neededBy)} · on Procurement › Site requests`
      });
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        setNotice('Stock or requests changed a moment ago — the list above is up to date now. Check it and send again.');
        loadPreview();
      } else {
        setError(getErrorMessage(err, 'Could not send the shortage'));
      }
    } finally {
      setSending(false);
    }
  };

  const lines = (preview?.shortage || []).filter((line) => line.to_request > 0);
  const allChecked = rows.length > 0 && ids.length === rows.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="shortage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg text-ink"><Send className="h-5 w-5 text-ink-3" aria-hidden="true" /> Send shortage to Procurement</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            Every BOQ line that free stock cannot cover becomes a purchase request on Procurement › Site requests. Lines that already have an open request are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <section aria-label="Projects">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[12px] font-semibold text-ink-3">Projects ({ids.length} of {rows.length})</span>
              <button type="button" className="text-[12px] font-semibold text-brand-ink hover:underline" onClick={() => setSelected(allChecked ? new Set() : new Set(rows.map((row) => row.id)))}>
                {allChecked ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <ul className="max-h-[260px] divide-y divide-line-2 overflow-y-auto rounded-xl border border-line">
              {rows.map((row) => (
                <li key={row.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-surface-hover">
                    <InvCheckbox checked={selected.has(row.id)} onCheckedChange={(checked) => toggle(row.id, Boolean(checked))} aria-label={`Include ${row.name}`} data-testid={`shortage-project-${row.id}`} />
                    <span className="min-w-0 flex-1 truncate font-semibold text-ink">{row.name}</span>
                    {row.system ? <SystemTag system={row.system} /> : null}
                    <span className="shrink-0 text-[12px] font-semibold tabular-nums text-status-red-ink">{formatQty(row.short)}</span>
                  </label>
                </li>
              ))}
            </ul>
            <label className="mt-3 block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-3">Needed on site by</span>
              <Input type="date" value={neededBy} onChange={(event) => setNeededBy(event.target.value)} data-testid="shortage-needed-by" />
            </label>
          </section>
          <section aria-label="What will be requested" aria-live="polite" className="min-w-0">
            <span className="mb-1.5 block text-[12px] font-semibold text-ink-3">What Procurement gets</span>
            {loading ? (
              <p className="flex items-center gap-2 rounded-xl border border-line px-3 py-6 text-[13px] text-ink-3"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Checking stock and open requests…</p>
            ) : preview ? (
              <div className="rounded-xl border border-line" data-testid="shortage-preview">
                <div className="border-b border-line-2 px-3 py-2.5">
                  <p className="text-[14px] text-ink">
                    <b className="font-bold">{plural(preview.request_lines, 'purchase request')}</b>
                    <span className="text-ink-2"> · {preview.request_by_unit ? formatUnitTotals(preview.request_by_unit) : formatQty(preview.request_qty)} to buy</span>
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-3">
                    {plural(preview.short_lines, 'line')} short vs stock
                    {preview.already_requested_lines ? ` · ${plural(preview.already_requested_lines, 'line')} already requested — skipped` : ''}
                  </p>
                </div>
                <ul className="max-h-[200px] divide-y divide-line-2 overflow-y-auto">
                  {lines.slice(0, 40).map((line) => (
                    <li key={`${line.project_id}-${line.part_number}`} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px]">
                      <span className="shrink-0 font-mono text-[12px] text-ink">{line.part_number}</span>
                      <span className="min-w-0 flex-1 truncate text-ink-3">{line.project_name}</span>
                      <b className="shrink-0 tabular-nums text-ink">{!line.unit || line.unit === 'pcs' ? pcsLabel(line.to_request) : `${formatQty(line.to_request)} ${line.unit}`}</b>
                    </li>
                  ))}
                  {!lines.length ? <li className="px-3 py-3 text-[12.5px] text-ink-3">Nothing to request — every short line already has an open request.</li> : null}
                </ul>
                {lines.length > 40 ? <p className="border-t border-line-2 px-3 py-1.5 text-[11.5px] text-ink-3">+ {plural(lines.length - 40, 'more line')}{preview.truncated ? ' (and more)' : ''}</p> : null}
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-3 py-6 text-center text-[13px] text-ink-3">Pick at least one project.</p>
            )}
          </section>
        </div>
        {notice ? <p className="text-[12.5px] font-semibold text-status-amber-ink" role="status" data-testid="shortage-notice">{notice}</p> : null}
        {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={send} disabled={sending || loading || !preview?.request_lines} data-testid="shortage-send">
            <ShoppingCart aria-hidden="true" />
            {sending ? 'Sending…' : preview?.request_lines ? `Send ${plural(preview.request_lines, 'request')}` : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
